import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import type { StatementBasis } from '../api/types';
import { STATEMENT_BASIS_LABELS, STATEMENT_BASIS_OPTIONS } from '../lib/statement-basis';
import { cn } from '../lib/utils';
import { Icon } from './icons';

/**
 * The data room's compact "Filter" menu, beside the search box (owner,
 * 2026-10-22: the "Accounting basis: Accrual (11)" chip row above the list was
 * clutter — the basis is now an option in a menu). Today it holds one group,
 * Accounting basis (ADR-0053): one choice per basis the room's financial
 * statements carry, with its count, that narrows the list to those statements
 * (chosen again: all documents). The applied choice also shows in the
 * "Showing:" bar with its own ✕. Renders nothing when no document has a basis.
 * A WAI-ARIA menu button: ↑/↓ move, Enter / Space choose, Escape closes and
 * returns focus to the button, a click outside closes it.
 */
export function DataRoomFilterMenu({
  basisCounts,
  basis,
  onBasisChange,
  className,
}: {
  basisCounts: Record<StatementBasis, number>;
  basis: StatementBasis | null;
  onBasisChange: (basis: StatementBasis | null) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const shown = STATEMENT_BASIS_OPTIONS.filter((b) => basisCounts[b] > 0 || b === basis);

  const items = () =>
    Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]') ?? []);

  useEffect(() => {
    if (!open) return;
    const list = items();
    (list.find((b) => b.getAttribute('aria-checked') === 'true') ?? list[0])?.focus();
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (shown.length === 0) return null;

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };
  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const list = items();
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    const move = (to: number) => list[(to + list.length) % list.length]?.focus();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(at + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(at - 1);
    } else if (e.key === 'Escape') {
      // Handled here so a surrounding dialog does not also close.
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      close(true);
    } else if (e.key === 'Tab') {
      close(false);
    }
  };

  return (
    <div ref={rootRef} className={cn('relative shrink-0', className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'inline-flex h-10 items-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-md:min-h-tap',
          basis
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-input bg-surface text-ink-2 hover:bg-surface-2',
        )}
      >
        Filter
        {basis ? <span aria-hidden="true">· 1</span> : null}
        {basis ? <span className="sr-only"> (1 applied)</span> : null}
        <Icon name="chevronDown" size={14} className={cn(open && 'rotate-180')} />
      </button>
      {open ? (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label="Filter documents"
          onKeyDown={onMenuKey}
          className="absolute right-0 top-full z-40 mt-1 min-w-[240px] rounded-md border bg-surface p-1 text-left shadow-md"
        >
          <p className="px-2.5 py-1.5 text-xs font-semibold text-ink-2" role="presentation">
            Accounting basis
          </p>
          {shown.map((b) => (
            <button
              key={b}
              type="button"
              role="menuitemradio"
              aria-checked={basis === b}
              tabIndex={-1}
              onClick={() => {
                close(true);
                onBasisChange(basis === b ? null : b);
              }}
              className="flex min-h-9 w-full items-center gap-2.5 whitespace-nowrap rounded-sm px-2.5 text-left text-sm text-ink hover:bg-surface-2 focus:bg-surface-2 focus:outline-none max-md:min-h-tap"
            >
              <span className="inline-flex w-4 justify-center">
                {basis === b ? <Icon name="check" size={14} /> : null}
              </span>
              {STATEMENT_BASIS_LABELS[b]} ({basisCounts[b]})
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
